import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Error as ErrorIcon, PlayArrow, Edit, FileCopy } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import EditScrapeModal from "./EditScrapeModal";
import XmlViewerModal from "./XmlViewerModal";
import { ScrapeConfig, UpdateScrapeConfig } from "../../Types/Scrapes";
import {
  useScrapeRunsQuery,
  useUpdateConfigMutation,
  useDeleteConfigMutation,
  useTriggerScrapeMutation,
} from "../../Queries/ScrapeQueries";
import { getScrapeRunXml } from "../../Services/API/Scrape";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "8px 24px 8px 48px",
    backgroundColor: "#fafafa",
  },
  card: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "4px 16px",
    alignItems: "center",
    padding: "12px 0",
  },
  topRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },
  scrapeName: {
    fontWeight: 600,
    whiteSpace: "nowrap" as const,
  },
  ats: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap" as const,
  },
  url: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    minWidth: 0,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    gridRow: "1 / 5",
    gridColumn: 2,
    flexShrink: 0,
  },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  label: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  failIcon: {
    color: theme.palette.error.main,
    fontSize: "16px",
  },
  date: {
    fontSize: "0.8rem",
  },
  stats: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  errorMsg: {
    fontSize: "0.8rem",
    color: theme.palette.error.main,
  },
  noOutput: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontSize: "0.8rem",
  },
  metadata: {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    fontFamily: "monospace",
  },
  divider: {
    marginLeft: 0,
    marginRight: 0,
  },
}));

interface ScrapeExpandedRowProps {
  configs: ScrapeConfig[];
}

export default function ScrapeExpandedRow({ configs }: ScrapeExpandedRowProps) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      {configs.map((config, index) => (
        <React.Fragment key={config.id}>
          {index > 0 && <Divider className={classes.divider} />}
          <ScrapeCard config={config} />
        </React.Fragment>
      ))}
    </div>
  );
}

function ScrapeCard({ config }: { config: ScrapeConfig }) {
  const classes = useStyles();
  const [editOpen, setEditOpen] = useState(false);
  const [xmlViewerOpen, setXmlViewerOpen] = useState(false);
  const [xmlContent, setXmlContent] = useState<string | null>(null);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [selectedBlobName, setSelectedBlobName] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data: runs = [], isLoading: runsLoading } = useScrapeRunsQuery(config.id, true);
  const updateMutation = useUpdateConfigMutation();
  const deleteMutation = useDeleteConfigMutation();
  const triggerMutation = useTriggerScrapeMutation();

  const latestRun = runs[0];

  const handleSave = (data: UpdateScrapeConfig) => {
    updateMutation.mutate(
      { id: config.id, data },
      {
        onSuccess: () => {
          setEditOpen(false);
          setSnackbar({ open: true, message: "Config updated", severity: "success" });
        },
        onError: () =>
          setSnackbar({ open: true, message: "Failed to update config", severity: "error" }),
      }
    );
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this scrape configuration?")) {
      deleteMutation.mutate(config.id, {
        onSuccess: () => {
          setEditOpen(false);
          setSnackbar({ open: true, message: "Config deleted", severity: "success" });
        },
        onError: () =>
          setSnackbar({ open: true, message: "Failed to delete config", severity: "error" }),
      });
    }
  };

  const handleTrigger = () => {
    triggerMutation.mutate(config.id, {
      onSuccess: () =>
        setSnackbar({ open: true, message: "Scrape triggered successfully", severity: "success" }),
      onError: () =>
        setSnackbar({ open: true, message: "Failed to trigger scrape", severity: "error" }),
    });
  };

  const handleCopyXmlUrl = async () => {
    const portalApiUrl = import.meta.env.VITE_PORTAL_API_URL;
    const url = `${portalApiUrl}/api/Scrape/GetScrapeXML/${config.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setSnackbar({ open: true, message: "GetScrapeXML URL copied", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to copy URL", severity: "error" });
    }
  };

  const handleViewXml = async (blobName: string) => {
    setSelectedBlobName(blobName);
    setXmlViewerOpen(true);
    setXmlLoading(true);
    try {
      const xml = await getScrapeRunXml(config.id, blobName);
      setXmlContent(xml);
    } catch {
      setXmlContent(null);
      setSnackbar({ open: true, message: "Failed to load XML", severity: "error" });
    } finally {
      setXmlLoading(false);
    }
  };

  return (
    <div className={classes.card}>
      <div className={classes.topRow}>
        <Typography variant="body2" className={classes.scrapeName}>
          {config.scrapeName || config.atsName}
        </Typography>
        {config.scrapeName && <span className={classes.ats}>{config.atsName}</span>}
      </div>
      <div className={classes.metadata}>
        ID: {config.id}
      </div>
      <span className={classes.url} title={config.url}>
        {config.url}
      </span>
      <div className={classes.actions}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Edit />}
          onClick={(e) => {
            e.stopPropagation();
            setEditOpen(true);
          }}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<PlayArrow />}
          onClick={(e) => {
            e.stopPropagation();
            handleTrigger();
          }}
          disabled={triggerMutation.isLoading}
        >
          {triggerMutation.isLoading ? "Triggering..." : "Run Now"}
        </Button>
      </div>
      <div className={classes.bottomRow}>
        <span className={classes.label}>Last Run:</span>
        {runsLoading ? (
          <CircularProgress size={14} />
        ) : !latestRun ? (
          <span className={classes.noOutput}>No output found</span>
        ) : (
          <>
            {latestRun.status !== "success" && <ErrorIcon className={classes.failIcon} />}
            <span className={classes.date}>
              {latestRun.scrapeDate
                ? new Date(latestRun.scrapeDate).toLocaleString()
                : "Unknown"}
            </span>
            {latestRun.status === "success" ? (
              <>
                <span className={classes.stats}>
                  {latestRun.successfulScrapes ?? 0} jobs | {latestRun.successRate ?? "N/A"}
                </span>
                <Button
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewXml(latestRun.blobName);
                  }}
                >
                  View XML
                </Button>
                <span className={classes.stats}>|</span>
                <Button
                  size="small"
                  color="primary"
                  startIcon={<FileCopy />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyXmlUrl();
                  }}
                >
                  Copy XML Download URL
                </Button>
              </>
            ) : (
              <span className={classes.errorMsg}>
                {latestRun.errorMessage || "Unknown error"}
              </span>
            )}
          </>
        )}
      </div>

      <EditScrapeModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        config={config}
        onSave={handleSave}
        onDelete={handleDelete}
        isSaving={updateMutation.isLoading}
      />

      <XmlViewerModal
        open={xmlViewerOpen}
        onClose={() => setXmlViewerOpen(false)}
        xml={xmlContent}
        isLoading={xmlLoading}
        blobName={selectedBlobName}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
